import { useEffect, useState } from 'react';

/**
 * Extracts a specific frame from a video URL as a data-URL poster image.
 *
 * Loads the video into an offscreen element with `crossOrigin="anonymous"`,
 * seeks to the target frame, paints it to a canvas, and returns the JPEG
 * data URL. Consumers pass that to `<video poster={...}>`.
 *
 * Falls back to `null` whenever anything blocks the extraction — CORS, no
 * canvas support, decode failure. In that case the consumer's `<video
 * preload="metadata">` will keep showing whatever the browser picks for
 * the default still (usually the first frame).
 *
 * @param {string|null} videoUrl
 * @param {{ frame?: number, fps?: number }} options
 *   - frame: which frame to capture (default 20)
 *   - fps:   assumed frame rate, since the <video> element doesn't expose
 *            this. Defaults to 30 (most common for web video). At 30fps,
 *            frame 20 ≈ 0.667 s.
 * @returns {string|null} data:image/jpeg;base64,... or null
 */
export default function useVideoPosterFrame(videoUrl, { frame = 20, fps = 30 } = {}) {
  const [posterDataUrl, setPosterDataUrl] = useState(null);

  useEffect(() => {
    if (!videoUrl) {
      setPosterDataUrl(null);
      return undefined;
    }
    let cancelled = false;
    const helper = document.createElement('video');
    helper.crossOrigin = 'anonymous';
    helper.muted = true;
    helper.playsInline = true;
    helper.preload = 'metadata';

    const targetTime = frame / fps;

    const onLoadedMetadata = () => {
      try {
        helper.currentTime = Math.min(
          targetTime,
          Number.isFinite(helper.duration) ? Math.max(helper.duration - 0.05, 0) : targetTime
        );
      } catch (_) { /* noop — browser may reject; we fall through */ }
    };

    const onSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = helper.videoWidth || 1280;
        canvas.height = helper.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(helper, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        if (!cancelled) setPosterDataUrl(dataUrl);
      } catch (e) {
        // Most commonly: SecurityError because the canvas got tainted by a
        // cross-origin video without proper CORS headers. Leave poster null
        // so the consumer's default-frame fallback wins.
      }
    };

    helper.addEventListener('loadedmetadata', onLoadedMetadata);
    helper.addEventListener('seeked', onSeeked);
    helper.src = videoUrl;

    return () => {
      cancelled = true;
      helper.removeEventListener('loadedmetadata', onLoadedMetadata);
      helper.removeEventListener('seeked', onSeeked);
      helper.src = '';
    };
  }, [videoUrl, frame, fps]);

  return posterDataUrl;
}
